import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '@/shared/utils/styling';

function TooltipProvider({
  delayDuration = 100,
  skipDelayDuration = 300,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      skipDelayDuration={skipDelayDuration}
      {...props}
    />
  );
}

interface TooltipProps
  extends React.ComponentProps<typeof TooltipPrimitive.Root> {
  showDuration?: number;
  onAutoHide?: () => void;
}

function Tooltip({ showDuration, onAutoHide, open, ...props }: TooltipProps) {
  // Auto-hide logic when tooltip is open
  React.useEffect(() => {
    if (open && showDuration && onAutoHide) {
      const timer = setTimeout(onAutoHide, showDuration);
      return () => clearTimeout(timer);
    }
  }, [open, showDuration, onAutoHide]);

  return <TooltipPrimitive.Root data-slot="tooltip" open={open} {...props} />;
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

interface TooltipContentProps
  extends React.ComponentProps<typeof TooltipPrimitive.Content> {
  variant?: 'default' | 'success' | 'error' | 'warning' | 'info';
  showArrow?: boolean;
  customBg?: string;
  customText?: string;
  customBorder?: string;
}

function TooltipContent({
  className,
  sideOffset = 4,
  children,
  variant = 'default',
  showArrow = false,
  customBg,
  customText,
  customBorder,
  ...props
}: TooltipContentProps) {
  const variantStyles = {
    default:
      'bg-background text-primary border-1 border-stroke px-[1.6rem] py-[0.4rem]',
    success: 'bg-green text-white border-green',
    error: 'bg-red text-white border-red',
    warning: 'bg-yellow text-primary border-yellow',
    info: 'bg-blue text-white border-blue',
  };

  const customStyles = {
    ...(customBg && { backgroundColor: customBg }),
    ...(customText && { color: customText }),
    ...(customBorder && { borderColor: customBorder }),
  };

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        style={customStyles}
        className={cn(
          'z-[9999] w-fit border border-secondary font-medium',
          'shadow-[0px_0px_24px_0px_rgba(24,26,33,0.06),0px_24px_48px_0px_rgba(24,26,33,0.06)]',
          variant === 'default' ? 'rounded-full' : 'rounded-lg',
          variantStyles[variant],
          'origin-[var(--radix-tooltip-content-transform-origin)]',
          'animate-in fade-in-0 zoom-in-95 duration-200',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
          'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          className
        )}
        {...props}>
        {children}
        {showArrow && <TooltipArrow variant={variant} />}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

interface TooltipArrowProps
  extends React.ComponentProps<typeof TooltipPrimitive.Arrow> {
  variant?: 'default' | 'success' | 'error' | 'warning' | 'info';
}

function TooltipArrow({
  className,
  variant = 'default',
  ...props
}: TooltipArrowProps) {
  const arrowStyles = {
    default: 'bg-white fill-white border-secondary',
    success: 'bg-green fill-green border-green',
    error: 'bg-red fill-red border-red',
    warning: 'bg-yellow fill-yellow border-yellow',
    info: 'bg-blue fill-blue border-blue',
  };

  return (
    <TooltipPrimitive.Arrow
      className={cn('z-[9999]', arrowStyles[variant], className)}
      {...props}
    />
  );
}

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipArrow,
  TooltipProvider,
};
